import { Fragment } from 'react';
import {
  Alert,
  Checkbox,
  Chip,
  Collapse,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  Edit as EditIcon,
  Info as InfoIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { GapAnalysisQuestionCard, ProtocolExpandedPanel } from '..';
import { Patient360TabPanel } from './Patient360TabPanel';
import { type GapDomain } from '../../../utils/patientGapAnalysisStore';
import { type Patient360ManagementTabsProps } from './types';

export const Patient360ManagementTabs = ({
  value,
  latestGapAnalysis,
  selectedPatientProtocols,
  expandedProtocolId,
  domainColors,
  onToggleExpandedProtocol,
  onShareProtocolChange,
  onEditProtocol,
  onUpdateProtocolInline,
  onShareGapQuestionChange,
  onAddClinicianComment,
  onEditGapQuestion,
  onDeleteGapQuestion,
}: Patient360ManagementTabsProps) => (
  <>
    <Patient360TabPanel value={value} index={7}>
      <Stack spacing={3}>
        <Alert severity="info" icon={<InfoIcon />}>Published protocols for this patient appear here. Use the checkbox to control whether each protocol is shared with the patient portal.</Alert>
        {selectedPatientProtocols.length === 0 ? (
          <Alert severity="warning" icon={<WarningIcon />}>No protocols have been published for this patient yet.</Alert>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead><TableRow><TableCell /><TableCell>Protocol</TableCell><TableCell>Status</TableCell><TableCell>Created Date</TableCell><TableCell>Start Date</TableCell><TableCell>End Date</TableCell><TableCell>Share Protocol With Patient</TableCell><TableCell>Edit</TableCell></TableRow></TableHead>
              <TableBody>
                {selectedPatientProtocols.map((protocol) => (
                  <Fragment key={protocol.id}>
                    <TableRow hover>
                      <TableCell width={48}><IconButton size="small" onClick={() => onToggleExpandedProtocol(protocol.id)}>{expandedProtocolId === protocol.id ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}</IconButton></TableCell>
                      <TableCell><Typography variant="subtitle2">{protocol.title}</Typography><Typography variant="body2" color="text.secondary">{protocol.description || 'No description'}</Typography></TableCell>
                      <TableCell><Chip size="small" color={protocol.status === 'ACTIVE' ? 'success' : 'default'} label={protocol.status === 'ACTIVE' ? 'Active' : 'Inactive'} /></TableCell>
                      <TableCell>{new Date(protocol.createdDate).toLocaleDateString()}</TableCell>
                      <TableCell>{protocol.startDate}</TableCell>
                      <TableCell>{protocol.endDate || 'Not set'}</TableCell>
                      <TableCell><Checkbox checked={Boolean(protocol.sharedWithPatient)} onChange={(event) => onShareProtocolChange(protocol.id, event.target.checked)} /></TableCell>
                      <TableCell><IconButton size="small" onClick={() => onEditProtocol(protocol)}><EditIcon fontSize="small" /></IconButton></TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ py: 0 }} colSpan={8}>
                        <Collapse in={expandedProtocolId === protocol.id} timeout="auto" unmountOnExit>
                          <ProtocolExpandedPanel protocol={protocol} onUpdate={onUpdateProtocolInline} />
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Stack>
    </Patient360TabPanel>

    <Patient360TabPanel value={value} index={8}>
      <Stack spacing={3}>
        {!latestGapAnalysis ? (
          <Alert severity="info" icon={<InfoIcon />}>No gap analysis has been published for this patient yet. Use the Patient Data Gap Analysis page to generate one.</Alert>
        ) : (
          <>
            <Alert severity="info" icon={<InfoIcon />}>
              Questions checked as patient-visible appear on the patient portal under Responses To Nutritionist Questions.
            </Alert>
            <Typography variant="subtitle2" color="text.secondary">Last published: {new Date(latestGapAnalysis.createdDate).toLocaleString()}</Typography>
            {(['CLINICAL', 'BEHAVIORAL', 'CULTURAL', 'FAMILY', 'SDOH'] as GapDomain[]).map((domain) => {
              const domainQuestions = latestGapAnalysis.questions.filter((question) => question.domain === domain && question.included);
              if (domainQuestions.length === 0) {
                return null;
              }

              return (
                <Paper key={domain} variant="outlined" sx={{ p: 2 }}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                    <Chip label={domain} color={domainColors[domain]} size="small" />
                    <Typography variant="subtitle1">{domain.charAt(0) + domain.slice(1).toLowerCase()} Questions</Typography>
                  </Stack>
                  <Stack spacing={1.5}>
                    {domainQuestions.map((question) => (
                      <GapAnalysisQuestionCard
                        key={question.id}
                        analysisId={latestGapAnalysis.id}
                        question={question}
                        domainColors={domainColors}
                        onShareChange={onShareGapQuestionChange}
                        onAddComment={onAddClinicianComment}
                        onEditQuestion={onEditGapQuestion}
                        onDeleteQuestion={onDeleteGapQuestion}
                      />
                    ))}
                  </Stack>
                </Paper>
              );
            })}
          </>
        )}
      </Stack>
    </Patient360TabPanel>
  </>
);